import React, { Fragment } from "react";
import { motion, useAnimation } from "motion/react";
import { IconCircleMinus } from "@tabler/icons-react";
import { Button, Badge, Separator } from "@lemonadetraining/web-ui";
import * as PRICING from "@lemonadetraining/web-shared/constants/Pricing";
import { useAppDispatch, cartActions } from "@lemonadetraining/web-shared";
import { formatSelectedOptionKey } from "@lemonadetraining/web-shared/utils/membershipLabels";
import type { Price } from "@lemonadetraining/contracts";

const artCoverVarients = {
  appear: { opacity: 1 },
};

interface CartItemImageProps {
  imageSrc?: string;
}

const CartItemImage = (props: CartItemImageProps) => {
  const artCoverAnimate = useAnimation();

  const imageLoaded = () => {
    artCoverAnimate.start("appear");
  };

  return (
    <Fragment>
      <motion.div
        key="image"
        whileHover={{ scale: 1.02 }}
        className="h-[60px] w-10 overflow-hidden rounded-[5px] shadow-[0px_2px_8px_rgba(0,0,0,0.15)] transition-all duration-300 hover:shadow-[0_20px_80px_-12.125px_rgba(0,0,0,0.3)]"
      >
        <motion.img
          initial={{ opacity: 0 }}
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }}
          src={props.imageSrc}
          variants={artCoverVarients}
          animate={artCoverAnimate}
          transition={{ duration: 0.5 }}
          onLoad={imageLoaded}
          width="100%"
        />
      </motion.div>
    </Fragment>
  );
};

interface CartItemProps {
  // The cart entry's identity. The checkout list passes the product's
  // `programUID` here, which is exactly what the cart reducer filters on when
  // removing an item.
  id: string;
  imageSrc?: string;
  title?: string;
  pricing: Price;
  selectedOptionKey?: string;
}

const CartItem = (props: CartItemProps) => {
  const dispatch = useAppDispatch();

  const onRemoveCartItem = () => {
    dispatch(cartActions.removeItem(props.id));
  };

  /* ----- Get badge variant based on membership type ----- */
  const getBadgeVariant = (selectedOptionKey?: string): "default" | "secondary" => {
    if (!selectedOptionKey) return "default";

    const parts = selectedOptionKey.split("_");
    if (parts.length >= 2 && parts[1] === "visit") {
      return "secondary"; // Black/gray for visit cards
    }
    return "default"; // Blue for recurring memberships
  };

  /* ----- Pricing label logic ----- */
  const getPricingLabel = (): string => {
    switch (props.pricing.type) {
      case PRICING.TYPE.free:
        return "FREE";

      case PRICING.TYPE.oneTime:
        return `${props.pricing.amount} €`;

      case PRICING.TYPE.subscription:
        // If we have selectedOptionKey (new products), badge shows duration, so just show amount
        // If no selectedOptionKey (old products), show "/month" to indicate recurring
        if (props.selectedOptionKey) {
          return `${props.pricing.amount} €`;
        }
        return `${props.pricing.amount} €/month`;

      default:
        return "ERROR";
    }
  };

  const pricingLabel = getPricingLabel();
  const selectedOptionLabel = formatSelectedOptionKey(props.selectedOptionKey);

  return (
    <div className="mr-2 mt-2 grid min-w-[300px] grid-cols-12 gap-2">
      <div className="col-span-2">
        <CartItemImage imageSrc={props.imageSrc} />
      </div>
      <div className="col-span-7">
        <div className="flex flex-col gap-0.5">
          <p className="text-base font-semibold">{props.title}</p>
          {selectedOptionLabel && (
            <Badge variant={getBadgeVariant(props.selectedOptionKey)} className="w-fit">
              {selectedOptionLabel}
            </Badge>
          )}
        </div>
      </div>
      <div className="col-span-2">
        <div className="flex flex-col items-end gap-0.5">
          <span className="text-sm">{pricingLabel}</span>
        </div>
      </div>
      <div className="col-span-1">
        <Button
          aria-label="Remove item from cart"
          size="icon-sm"
          variant="destructive"
          onClick={onRemoveCartItem}
        >
          <IconCircleMinus />
        </Button>
      </div>
      <div className="col-span-12">
        <Separator />
      </div>
    </div>
  );
};

export default CartItem;
